import { watch } from 'vue'
import { endAgentSession } from '~/services/agent-session'
import { useAuthStore } from '~/store/auth'
import { clearSavedGridFilters, syncGridFiltersToLogin } from '~/utils/grid-filter-state'

// sessionStorage is per tab, but the login is shared through localStorage. So a
// session can end while this tab does nothing. This plugin handles those cases;
// see services/agent-session.js for the others.

// The persisted auth store's localStorage key (the store's ID).
const AUTH_STORAGE_KEY = 'auth'

/** The login token in localStorage, or null. It reads storage, because this tab's store can be out of date. */
function storedAccessToken() {
  try {
    const token = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY))?.access_token

    return typeof token === 'string' ? token : null
  } catch {
    return null
  }
}

export default defineNuxtPlugin({
  name: 'session-end-sync',
  setup() {
    // The session can have ended, or another agent can have logged in, while this tab's page was not loaded.
    syncGridFiltersToLogin(storedAccessToken())

    const authStore = useAuthStore()

    // A login or a sign-out in this tab. It is synchronous, so it runs before a grid page can save values.
    watch(
      () => authStore.access_token,
      (token) => syncGridFiltersToLogin(token),
      { flush: 'sync' },
    )

    function followLoginInOtherTabs() {
      const token = storedAccessToken()

      if (token === authStore.access_token) return

      if (token === null) {
        endAgentSession()
      } else {
        // A new login in another tab. Load it here: this tab's store is out of
        // date, and its next write would replace the new login in localStorage.
        clearSavedGridFilters()
        window.location.reload()
      }
    }

    window.addEventListener('storage', (event) => {
      // A null key means another tab cleared all of localStorage.
      if (event.key === AUTH_STORAGE_KEY || event.key === null) followLoginInOtherTabs()
    })

    // A page that the browser restores from its back/forward cache got no storage events.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) followLoginInOtherTabs()
    })
  },
})
